import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { sortedByName } from 'src/store/utilities.js';
import type { User } from 'src/store/types.js';
import type { OrganizationWithData } from 'src/app-state-dashboard.js';
import './d-select-organization-dashboard.js';

/**
 * Dashboard for NCC - viser organisasjonsliste med mer info og NCC logo.
 * Uten "Legg til ny organisasjon" knapp.
 *
 * @fires request-logout
 */
@customElement('d-dashboard')
export class DDashboard extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      min-height: 100vh;
      background: var(--outskirts-background-color);
      padding-top: 0px;
    }

    .outskirts {
      margin: 0 auto;
      height: 100vh;
      overflow: auto;
    }

    .outskirtsHeader {
      margin: 0 auto;
      padding: 0 20px;
      width: 768px;
      max-width: var(--appWidth);
      min-width: 360px;
      box-sizing: border-box;
    }

    .outskirtsHeader > div {
      display: flex;
      justify-content: space-between;
      position: relative;
      color: white;
      height: var(--applicationHeaderHeight);
      box-sizing: border-box;
      border-bottom: 1px solid hsla(0, 0%, 100%, 0.2);
    }

    .outskirtsHeader > div > div {
      display: flex;
    }

    .outskirtsHeader .logo {
      flex: none;
      width: 137px;
      height: 45px;
      margin-bottom: -5px;
      background: url(/images/trinnvis-logo-white.svg) -2px 10px no-repeat;
      background-size: 97px 25px;
      text-indent: -10000px;
    }

    .outskirtsHeader a {
      font-size: 13px;
      padding: 16px 20px 0 16px;
      height: 100%;
      opacity: 0.7;
      box-sizing: border-box;
      text-decoration: none;
      cursor: hand;
    }

    .outskirtsHeader a:last-child {
      margin-right: -20px;
    }

    .outskirts a {
      color: white;
    }

    .outskirts h1 {
      font-size: 24px;
      font-weight: 500;
      line-height: 120%;
      color: white;
      margin-bottom: 20px;
    }

    .outskirtsContent {
      padding-top: 20px;
      padding-bottom: 20px;
      margin: 0 auto;
      width: 768px;
      padding-left: 20px;
      padding-right: 20px;
      max-width: var(--appWidth);
      min-width: 360px;
      box-sizing: border-box;
    }

    .ncc-logo-container {
      display: flex;
      justify-content: center;
      padding: 40px 0 30px 0;
    }

    .ncc-logo {
      width: 120px;
      height: auto;
      filter: brightness(0) invert(1);
    }

    .header-with-export {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
    }

    .header-with-export h1 {
      margin-bottom: 0;
    }

    .export-links {
      display: flex;
      gap: 8px;
      align-items: center;
      font-size: 13px;
    }

    .export-link {
      color: rgba(255, 255, 255, 0.8);
      text-decoration: none;
      padding: 6px 12px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      transition: all 0.2s ease;
    }

    .export-link:hover {
      color: white;
      background-color: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .export-separator {
      opacity: 0.4;
    }

    /* Beta ribbon */
    .beta-ribbon {
      position: fixed;
      top: 0;
      right: 0;
      width: 150px;
      height: 150px;
      overflow: hidden;
      z-index: 9999;
      pointer-events: none;
    }

    .beta-ribbon span {
      position: absolute;
      display: block;
      width: 225px;
      padding: 10px 0;
      background-color: rgba(255, 200, 0, 0.9);
      box-shadow: 0 5px 10px rgba(0, 0, 0, 0.3);
      color: #000;
      font-size: 13px;
      font-weight: 700;
      text-transform: uppercase;
      text-align: center;
      letter-spacing: 1px;
      right: -50px;
      top: 35px;
      transform: rotate(45deg);
    }
  `;

  @property({ type: Object })
  user!: User;

  @property({ type: Array })
  organizationsWithData: OrganizationWithData[] = [];

  render() {
    return html`
      <div class="outskirts">
        <!-- Beta ribbon -->
        <div class="beta-ribbon">
          <span>${t('dashboard.betaRibbon')}</span>
        </div>

        <div class="outskirtsHeader">
          <div>
            <div class="logo">Trinnvis</div>
            ${!this.user ? nothing : html`<a href="/" @click=${() => this.handleLogout()}>${t('topMenu.logout')}</a>`}
          </div>
        </div>
        <div class="outskirtsContent">
          <div class="ncc-logo-container">
            <img src="/images/ncc-logo.svg" alt="NCC" class="ncc-logo" />
          </div>
          <div class="header-with-export">
            <h1>${t('dashboard.selectOrganization')}</h1>
            <div class="export-links">
              <a href="#" @click=${this.handleExportExcel} class="export-link">${t('dashboard.exportToExcel')}</a>
              <span class="export-separator">|</span>
              <a href="#" @click=${this.handleExportPdf} class="export-link">${t('dashboard.exportToPdf')}</a>
            </div>
          </div>
          <d-select-organization-dashboard
            .organizations=${sortedByName(this.organizationsWithData)}
            .user=${this.user}
          >
          </d-select-organization-dashboard>
        </div>
      </div>
    `;
  }

  private handleLogout() {
    this.dispatchEvent(
      new CustomEvent('request-logout', { bubbles: true, composed: true, detail: { redirect: true } }),
    );
  }

  private handleExportExcel(e: Event) {
    e.preventDefault();
    alert(t('dashboard.exportToExcelComingSoon'));
  }

  private handleExportPdf(e: Event) {
    e.preventDefault();
    alert(t('dashboard.exportToPdfComingSoon'));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-dashboard': DDashboard;
  }
}
